<template>
  <div v-if="!field.isHidden" :data-test="name" class="form-input-set" :class="{ 'decoration-negative': isError }">
    <label :for="'input_' + name" :style="isError ? 'margin-bottom: 0;' : null">
      <span v-html="legend"></span>
    </label>
    <transition name="slide">
      <span
        v-if="isError"
        :id="'input_error_hint_' + name"
        style="display: block; margin-bottom: 3px; font-size: 18px;"
        class="text-negative"
        v-html="$to(internalHint)"
      ></span>
    </transition>
    <input
      :id="'input_' + name"
      ref="input"
      :type="type"
      :value="internalValue"
      class="form-input"
      v-bind="$attrs"
      :disabled="isDisabled"
      :aria-required="required"
      :required="labelAsRequired"
      :aria-invalid="isError ? true : null"
      :aria-describedby="isError ? 'input_error_hint_' + name : null"
      @input="updateValue($event.target.value)"
      @change="checkValue"
      @focus="onFocus"
      @blur="onBlur"
    />
  </div>
</template>

<script>
import { emptyFormElement } from '@/utils/form';

export default {
  name: 'FormInput',

  inject: {
    parentForm: {
      from: 'parentForm',
      default: null,
    },
  },

  inheritAttrs: false,

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  props: {
    name: {
      type: String,
      required: true,
    },

    label: {
      type: String,
      required: false,
      default: null,
    },

    hint: {
      type: String,
      required: false,
      default: null,
    },

    modelValue: {
      type: String,
      required: false,
      default: '',
    },

    required: {
      type: Boolean,
      required: false,
    },

    labelAsRequired: {
      type: Boolean,
      required: false,
    },

    form: {
      type: Object,
      required: false,
      default: null,
    },

    type: {
      type: String,
      required: false,
      default: 'text',
    },
  },

  data() {
    return {
      internalValue: this.modelValue,
      isBeingChanged: false,
      internalHint: null,
    };
  },

  computed: {
    state() {
      if (this.parentForm) return this.parentForm.form;
      return this.form;
    },

    field() {
      if (!this.state || !this.state[this.name]) return emptyFormElement();
      return this.state[this.name];
    },

    fieldHasFocus() {
      return this.field.hasFocus;
    },

    isError() {
      return !this.isBeingChanged && !this.field.isValid && this.field.isChecked;
    },

    isDisabled() {
      return this.state.isDisabled;
    },

    legend() {
      if (this.label != null){
        if (this.required){
          return this.label + "<span style='color: red'>*</span>";
        } else {
          return this.label;
        }
      } else if (this.required){
        return "<span style='color: red'>*</span>";
      } else {
        return "";
      }
    },
  },

  watch: {
    fieldHasFocus: 'checkFocus',
  },

  mounted() {
    if (!this.field.isHidden) {
      this.internalValue = this.field.value || this.internalValue;
      this.defaultValue = this.internalValue;
      this.field.setInvalid = this.setInvalid;
      this.field.checkValidation = this.checkValue;
      this.field.resetValue = this.resetValue;
      this.$set(this.field, 'name', this.name);
      this.$nextTick(() => {
        this.checkValue();
        this.checkFocus();
        this.field.isInit = true;
        if (this.field.promise) this.field.resolve();
      });
    }
  },

  methods: {
    setInvalid(msg) {
      this.internalHint = msg || this.hint;
      this.field.isValid = false;
    },

    checkFocus() {
      if (this.fieldHasFocus && this.$refs.input) {
        this.field.hasFocus = false;
        this.$refs.input.focus();
      }
    },

    updateValue(val) {
      if (this.internalValue !== val) {
        this.isBeingChanged = true;
        this.internalValue = val;
      }
      this.$emit('update:modelValue', this.internalValue);
    },

    checkValue() {
      this.isBeingChanged = false;
      this.field.filters.forEach(filter => {
        this.internalValue = filter(this.internalValue, this.state);
      });

      let isValid = true;
      if (this.required && (!this.internalValue || !this.internalValue.length)) {
        isValid = false;
      }
      this.internalHint = this.hint;
      // eslint-disable-next-line no-bitwise
      isValid &= !this.field.validators.some(v => {
        const error = !v.handler(this.internalValue, this.state);
        if (error && v.hint) this.internalHint = v.hint;
        return error;
      });
      this.$set(this.field, 'isValid', isValid);
      this.$set(this.field, 'value', this.internalValue);
      this.$emit('update:modelValue', this.internalValue);
    },

    resetValue() {
      this.internalValue = this.defaultValue;
      this.checkValue();
    },

    onFocus() {
      this.$emit('focus');
    },

    onBlur() {
      this.$emit('blur');
    },
  },
};
</script>
